import { useContext } from 'react'
import { parse } from 'query-string'

import { isThreeDSecureError } from 'utils/three-d-secure-helper/is-three-d-secure-error'
import { RecurlyPayment, updateUserPaymentMethod } from 'api'
import { useRecurly } from 'lib/recurly'
import { useUser } from 'hooks/use-user'
import { RecurlyContext } from 'features/checkout/providers'
import { handleThreeDSecure } from 'features/checkout/utils'
import { Mixpanel } from 'utils/mixpanel'
import { EVENTS, trackEvent } from 'utils/tracking/events-tracker'
import { RecurlyErrorResponse } from 'lib/recurly/types'

import { THREE_D_ELEMENT_ID } from '../../components/form/consts'

const isRecurlyErrorResponse = (
  error: unknown,
): error is RecurlyErrorResponse => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { data?: { error?: string } } }).response
      ?.data?.error === 'string'
  )
}

type LocationSearchParams = {
  next?: string
}

interface HandleUpdateParams {
  token: RecurlyPayment
  threeDSecureToken?: string
  checkoutSuccessSource: string
  paymentMethod?: string
}

export const useUpdate = () => {
  const context = useContext(RecurlyContext)

  if (!context) {
    throw new Error('useRecurly must be used within a RecurlyContainer')
  }

  const { setIsLoading, setShowThreeDAuth, setServerError, onUpdateSuccess } =
    context

  const { next = '' }: LocationSearchParams = parse(window.location.search)

  const recurly = useRecurly()
  const user = useUser()

  let observer: MutationObserver | null = null

  const addObserver = () => {
    observer = new MutationObserver(() => {
      const iframe = document.querySelector(
        `#${THREE_D_ELEMENT_ID} > div > iframe`,
      )
      if (iframe && iframe.getAttribute('scrolling') === 'no') {
        iframe.setAttribute('scrolling', 'yes')
      }
    })

    const container = document.getElementById(THREE_D_ELEMENT_ID)
    if (!container) return
    observer.observe(container, {
      subtree: true,
      childList: true,
    })
  }

  const removeObserver = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
  }

  const handleUpdate = async (args: HandleUpdateParams) => {
    const {
      token,
      threeDSecureToken = undefined,
      checkoutSuccessSource,
      paymentMethod,
    } = args
    const mixpanelProps = {}

    setServerError(null)

    try {
      const res = await updateUserPaymentMethod({
        tokenId: token.id,
        threeDSecureToken,
      })
      if (res.status === 204) {
        Mixpanel.trackUpdateBillingInformationUpdate({
          paymentMethod: paymentMethod || token.type,
        })
        trackEvent({
          event: EVENTS.UPDATE_BILLING_INFORAMTION_UPDATED,
        })

        if (next) {
          if (user.subscription?.subscriptionStatus === 'past_due')
            localStorage.setItem('pstd', `${new Date()}`)

          const nextUrl = new URL(next, window.location.origin)
          if (!next.includes('/pricing'))
            nextUrl.searchParams.set('openPlanModal', 'true')

          window.location.href = nextUrl.toString()
          return
        }
        setIsLoading(false)
        if (onUpdateSuccess) onUpdateSuccess()
      }
    } catch (error) {
      if (!isRecurlyErrorResponse(error)) return
      const err = error
      setIsLoading(false)
      if (err.response) {
        Mixpanel.trackUpdateBillingInformationError({
          errorName: err.response.data.error,
          errorDescription: err.response.data.description.message,
          paymentMethod,
        })
        const { code } = err.response.data
        if (isThreeDSecureError(code)) {
          return handleThreeDSecure({
            recurly,
            error: err,
            removeObserver,
            token,
            mixpanelProps,
            addObserver,
            setShowThreeDAuth,
            setIsLoading,
            setServerError,
            checkoutSuccessSource,
            handleRequest: handleUpdate,
          })
        }
      }
      setServerError({
        type: 'error',
        message: 'request_failed',
      })
    }
  }

  return { handleUpdate }
}
